
import React, { useState, useEffect } from 'react';
import { UseFormReturn } from "react-hook-form";
import { TerraceFormValues } from "../SubmissionFormSchema";
import { SubmissionService } from '@/lib/services/submissionService';
import { toast } from "sonner";
import PhotoUrlExtractor from './PhotoUrlExtractor';
import PhotoFormField from './PhotoFormField';
import PhotoSelector from './PhotoSelector';
import PhotoPreview from './PhotoPreview';

interface TerracePhotoManagerProps {
  form: UseFormReturn<TerraceFormValues>;
  submissionId?: string;
  googlePlaceId?: string;
  placeName?: string;
}

interface PhotoReference {
  photo_reference: string;
  cached_url: string | null;
  width: number;
  height: number;
  attribution: string;
  is_cached: boolean;
  photo_source: string;
  source_priority: number;
  is_main_photo?: boolean;
  cached_photo_id?: string;
}

const TerracePhotoManager: React.FC<TerracePhotoManagerProps> = ({ 
  form, 
  submissionId,
  googlePlaceId,
  placeName 
}) => {
  const [photos, setPhotos] = useState<PhotoReference[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const photoUrl = form.watch('photo_url');

  const fetchPhotos = async () => {
    if (!submissionId || !googlePlaceId || !placeName) return;
    
    setIsLoading(true);
    try {
      const fetchedPhotos = await SubmissionService.fetchPhotosForSubmission(
        submissionId, 
        googlePlaceId, 
        placeName
      );
      setPhotos(fetchedPhotos);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Failed to fetch photos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (submissionId && googlePlaceId && placeName) {
      fetchPhotos();
    }
  }, [submissionId, googlePlaceId, placeName]);

  const shouldShowRefreshButton = !!(submissionId && googlePlaceId && placeName);

  return (
    <div className="space-y-4">
      <PhotoUrlExtractor form={form} />

      <PhotoFormField 
        form={form}
        onRefreshPhotos={fetchPhotos}
        isLoading={isLoading}
        showRefreshButton={shouldShowRefreshButton}
      />

      <PhotoSelector
        form={form}
        photos={photos}
        setPhotos={setPhotos}
        submissionId={submissionId}
        placeName={placeName}
        isLoading={isLoading}
      />

      <PhotoPreview photoUrl={photoUrl} />
    </div>
  );
};

export default TerracePhotoManager;
